import { useCallback, useContext, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const fileToBase64Only = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => {
      const result = String(reader.result || "");

      resolve(result.includes(",") ? result.split(",")[1] : result);
    };

    reader.onerror = () => reject(new Error("Unable to read file"));
  });

const useMedia = () => {
  const { userID, api, anlookApi } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);

  /* ===============================
     PHP IMAGE UPLOAD - ANLOOK API
  =============================== */

  const uploadImage = useCallback(
    async (imageName, base64String, type = "IMG") => {
      try {
        setLoading(true);

        if (!anlookApi) {
          throw new Error("Anlook API instance missing");
        }

        if (!userID) {
          throw new Error("User ID missing");
        }

        if (!base64String) {
          throw new Error("Image data missing");
        }

        const response = await anlookApi.post("/v1/uploadItemImagev2.php", {
          userid: String(userID),
          item_name: imageName || "media_image",
          type,
          image_data: base64String,
        });

        if (
          String(response?.data?.status) === "0" &&
          response?.data?.image_url
        ) {
          return response.data;
        }

        throw new Error(
          response?.data?.message ||
            response?.data?.responseMessage ||
            "Image upload failed",
        );
      } catch (error) {
        console.error("Upload Image Error:", error?.response?.data || error);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [anlookApi, userID],
  );

  /* ===============================
     VIDEO UPLOAD - NORMAL API
  =============================== */

  const uploadVideo = useCallback(
    async (file) => {
      try {
        setLoading(true);

        if (!file) {
          throw new Error("Please select video");
        }

        if (!userID) {
          throw new Error("User ID missing");
        }

        const base64 = await fileToBase64Only(file);

        const response = await api.post("/Whatsapp/uploadMedia", {
          userid: String(userID),
          mediaType: "video",
          mediaData: base64,
        });

        return response?.data || null;
      } catch (error) {
        console.error("Upload Video Error:", error?.response?.data || error);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, api],
  );

  /* ===============================
     DOCUMENT UPLOAD - NORMAL API
  =============================== */

  const uploadDocument = useCallback(
    async (file) => {
      try {
        setLoading(true);

        if (!file) {
          throw new Error("Please select document");
        }

        if (!userID) {
          throw new Error("User ID missing");
        }

        const base64 = await fileToBase64Only(file);

        const response = await api.post("/Whatsapp/uploadMedia", {
          userid: String(userID),
          mediaType: "pdf",
          mediaData: base64,
        });

        return response?.data || null;
      } catch (error) {
        console.error("Upload Document Error:", error?.response?.data || error);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, api],
  );

  /* ===============================
     PHP EXCEL UPLOAD - ANLOOK API
  =============================== */

  const uploadCampaignExcel = useCallback(
    async (file) => {
      try {
        setLoading(true);

        if (!file) {
          throw new Error("Please select Excel file");
        }

        if (!anlookApi) {
          throw new Error("Anlook API instance missing");
        }

        if (!userID) {
          throw new Error("User ID missing");
        }

        const base64 = await fileToBase64Only(file);

        const payload = {
          userid: String(userID),
          file_name: file.name?.replace(/\.[^/.]+$/, "") || "campaign_excel",
          file_data: base64,
          type: "XLSX",
        };

        const response = await anlookApi.post(
          "/v1/uploadCampaignExcelv2.php",
          payload,
        );

        return response?.data || null;
      } catch (error) {
        console.error("Upload Excel Error:", error?.response?.data || error);

        return null;
      } finally {
        setLoading(false);
      }
    },
    [anlookApi, userID],
  );

  /* ===============================
     WHATSAPP IMAGE - NORMAL API
  =============================== */

  const uploadMediaImage = useCallback(
    async (imageName, base64String) => {
      try {
        setLoading(true);

        if (!userID) {
          throw new Error("User ID missing");
        }

        const response = await api.post("/Whatsapp/uploadMedia", {
          userid: String(userID),
          mediaType: "img",
          mediaData: base64String,
        });

        return response?.data || null;
      } catch (error) {
        console.error(
          "Upload Media Image Error:",
          error?.response?.data || error,
        );

        return null;
      } finally {
        setLoading(false);
      }
    },
    [userID, api],
  );

  return {
    uploadImage,
    uploadVideo,
    uploadDocument,
    uploadCampaignExcel,
    uploadMediaImage,
    loading,
  };
};

export default useMedia;
